/**
 * ApplicationDetailModal
 * Modal con todo el detalle de una solicitud (usuario, solicitud, desglose
 * de comision, link a contrato). Reemplaza la navegacion directa a Perfil
 * Financiero desde la lista de solicitudes.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { CopyButton } from '@/components/ui/CopyButton';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { FeeBreakdown } from '@/features/credit/components/FeeBreakdown';
import { SignedContractViewerModal } from '@/features/credit/components/SignedContractViewerModal';
import { useApplicationDetail } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';

interface ApplicationDetailModalProps {
  isOpen: boolean;
  applicationId: string;
  directoryId: string;
  onClose: () => void;
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'SIGNED':
    case 'DISBURSED':
      return 'success';
    case 'PENDING_SIGNATURE':
    case 'CREATED':
      return 'warning';
    case 'EXPIRED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'default';
  }
}

function formatDateLong(dateStr?: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function InfoRow({
  label,
  value,
  title,
  truncate,
}: {
  label: string;
  value: React.ReactNode;
  title?: string | undefined;
  truncate?: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-right text-sm font-medium tabular-nums text-foreground ${
          truncate ? 'min-w-0 truncate' : ''
        }`}
        title={title}
      >
        {value ?? '-'}
      </span>
    </div>
  );
}

export function ApplicationDetailModal({
  isOpen,
  applicationId,
  directoryId,
  onClose,
}: ApplicationDetailModalProps) {
  const { application, user, isLoading, isError, refetch } = useApplicationDetail(
    applicationId,
    directoryId,
  );
  const [showContract, setShowContract] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle de Solicitud"
      titleAction={
        applicationId ? <CopyButton value={applicationId} label="Application ID" /> : undefined
      }
      size="lg"
    >
      <div className="space-y-4">
        {isLoading && <ModalSkeleton />}

        {isError && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-10">
            <AlertTriangle size={36} className="mb-3 text-muted-foreground" />
            <h3 className="text-base font-semibold text-foreground">
              Error al cargar la solicitud
            </h3>
            <Button className="mt-4" onClick={() => refetch()}>
              <RefreshCw size={16} />
              Reintentar
            </Button>
          </div>
        )}

        {application && !isLoading && !isError && (
          <>
            {/* Row 1: Usuario + Solicitud */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card title="Usuario">
                <div className="space-y-1">
                  {user ? (
                    <>
                      <InfoRow label="Nombre" value={user.full_name && user.full_name !== 'N/A' ? user.full_name : '—'} />
                      <InfoRow label="Telefono" value={user.phone_number} />
                      <InfoRow
                        label="Directory ID"
                        value={
                          <CopyButton value={user.directory_id} label="Directory ID" variant="plain" />
                        }
                      />
                      <InfoRow
                        label="People ID"
                        value={
                          <CopyButton value={user.people_id} label="People ID" variant="plain" />
                        }
                      />
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin info de usuario</p>
                  )}
                </div>
              </Card>

              <Card title="Solicitud">
                <div className="space-y-1">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Estado</span>
                    <Badge variant={getStatusVariant(application.status)}>
                      {application.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Nivel</span>
                    {application.credit_level &&
                    Object.values(CreditLevel).includes(application.credit_level as CreditLevel) ? (
                      <CreditLevelBadge level={application.credit_level as CreditLevel} size="sm" />
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {application.credit_level ?? '-'}
                      </span>
                    )}
                  </div>
                  <InfoRow label="Plazo" value={`${application.term_weeks} semanas`} />
                  <InfoRow
                    label="Tasa Anual"
                    value={`${(application.annual_rate * 100).toFixed(1)}%`}
                  />
                  <InfoRow
                    label="Pago Semanal"
                    value={formatCurrency(application.weekly_payment_mxn, 'MXN', 'es-MX')}
                  />

                  <div className="mt-2 border-t border-border pt-2">
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-muted-foreground">Oferta origen</span>
                      <Link
                        to={`/credit/offers/${application.offer_id}?directoryId=${directoryId}`}
                        onClick={onClose}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {application.offer_id.slice(0, 8)}...
                      </Link>
                    </div>
                    {application.contract_view_url && (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-sm text-muted-foreground">Contrato</span>
                        <button
                          type="button"
                          onClick={() => setShowContract(true)}
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                        >
                          <FileText size={12} />
                          Ver contrato
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-2 border-t border-border pt-2">
                    <InfoRow label="Expira" value={formatDateLong(application.expires_at)} />
                    <InfoRow label="Firmada" value={formatDateLong(application.signed_at)} />
                    <InfoRow label="Creada" value={formatDateLong(application.created_at)} />
                    <InfoRow label="Actualizada" value={formatDateLong(application.updated_at)} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Row 2: Desglose del monto financiado */}
            <Card
              title="Monto financiado"
              description="Desglose del calculo: monto solicitado, comision apertura y monto a amortizar"
            >
              <FeeBreakdown
                requestedAmountMxn={application.amount_mxn}
                openingFeeMxn={application.opening_fee_mxn}
                openingFeeIvaMxn={application.opening_fee_iva_mxn}
                openingFeePct={application.opening_fee_pct}
                financedAmountMxn={application.financed_amount_mxn}
              />
            </Card>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3 border-t border-border pt-3">
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </>
        )}
      </div>

      {application?.contract_view_url && (
        <SignedContractViewerModal
          isOpen={showContract}
          onClose={() => setShowContract(false)}
          title={`Contrato — Solicitud ${applicationId.slice(0, 8)}...`}
          url={application.contract_view_url}
          isLoading={false}
        />
      )}
    </Modal>
  );
}

function ModalSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton variant="rectangular" height={220} />
        <Skeleton variant="rectangular" height={220} />
      </div>
      <Skeleton variant="rectangular" height={120} />
    </div>
  );
}
